import type { SortingState } from '@tanstack/react-table';
import { Brand } from '../../components/product-table/columns/brand-column';
import { Department } from '../../components/product-table/columns/department-column';
import { Description } from '../../components/product-table/columns/description-column';
import { Done } from '../../components/product-table/columns/done-column';
import { LastReceived } from '../../components/product-table/columns/last-received-column';
import { ProductNameWithLabels } from '../../components/product-table/columns/product-name-with-labels-column';
import { QOH } from '../../components/product-table/columns/qoh-column';
import { getUnitsSoldColumn } from '../../components/product-table/columns/sales-columns';
import { Size } from '../../components/product-table/columns/size-column';
import { SizeOunces } from '../../components/product-table/columns/size-ounces-column';
import { Type } from '../../components/product-table/columns/type-column';
import { VendorName } from '../../components/product-table/columns/vendor-name-column';
import type { StrictColumnDef } from '../../components/tanstack-table/column';
import { ReportActions } from '../../components/tanstack-table/report-actions';
import { TanStackTable } from '../../components/tanstack-table/table';
import { useRouteRowSelection } from '../../components/tanstack-table/use-route-row-selection';
import { useTableRef } from '../../components/tanstack-table/use-table-ref';
import { PageToolbar } from '../../components/toolbar/page-toolbar';
import { SearchWithCount } from '../../components/toolbar/search-with-count';
import { ProductTableContext } from '../../contexts/product-table-context';
import type { Product } from '../../data/product';
import { type Filters, useFilteredProducts } from '../../hooks/products';
import { productReportsLayout } from '../../router';

// Display columns
const displayColumns: StrictColumnDef<Product>[] = [
  Department,
  VendorName,
  ProductNameWithLabels,
  Type,
  Size,
  QOH,
  LastReceived,
  getUnitsSoldColumn(1),
];

// Sort columns (includes hidden columns for sorting)
const allColumnsForSort: StrictColumnDef<Product>[] = [
  ...displayColumns,
  SizeOunces,
  Brand,
  Description,
  Done, // Include Done column for print but hidden
];

// Multi-column sort order
const sorting: SortingState = [
  { id: Department.id, desc: false },
  { id: SizeOunces.id, desc: true },
  { id: Type.id, desc: false },
  { id: Brand.id, desc: false },
  { id: Description.id, desc: true },
];

// Column visibility: hide the sort-only columns and Done column
const columnVisibility = {
  [SizeOunces.id]: false,
  [Brand.id]: false,
  [Description.id]: false,
  [Done.id]: false,
};

const defaultFilters: Filters = { soldIn: 1, minimumInventory: 1 };

export function PreviousDaySalesReport() {
  const products = productReportsLayout.useLoaderData();
  const { filters, setFilters, filteredProducts, baselineCount } = useFilteredProducts(
    products,
    defaultFilters
  );
  const { rowSelection, onRowSelectionChange, onRowClick } = useRouteRowSelection();
  const [table, onTableReady] = useTableRef<Product>();

  return (
    <>
      <ProductTableContext.Provider value={{ filters, setFilters, filteredProducts }}>
        <PageToolbar
          right={
            <>
              <SearchWithCount
                quantity={filteredProducts.length}
                total={baselineCount}
                noun="products"
              />
              <ReportActions table={table} title="Previous Day Sales" />
            </>
          }
        />
      </ProductTableContext.Provider>
      <TanStackTable
        data={filteredProducts}
        columns={allColumnsForSort}
        sorting={sorting}
        columnVisibility={columnVisibility}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        onRowClick={onRowClick}
        getRowId={(product) => product.id}
        onTableReady={onTableReady}
      />
    </>
  );
}
